import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../services/api';

/**
 * Avisos de tareas (docs/avisos-de-tareas-plan.md): cuando a la empresa le dejan
 * una anotación como tarea, se le avisa al momento. La que va a toda la empresa,
 * a estos correos y a este Slack; la que va a una persona, a su propio correo.
 *
 * `companyId` es la empresa del perfil: así el admin lo configura por un cliente.
 * El webhook de Slack es un secreto: el servidor nunca lo devuelve entero, solo
 * si está puesto y su final.
 */
const TaskNotifications = ({ companyId, isAdmin = false }) => {
  const [datos, setDatos] = useState(null);
  const [correos, setCorreos] = useState('');
  const [webhook, setWebhook] = useState('');
  const [guardando, setGuardando] = useState(false);
  const params = isAdmin ? { company_id: companyId } : {};

  const aplicar = d => {
    setDatos(d);
    setCorreos((d.emails || []).join(', '));
    setWebhook('');
  };

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/task-notifications', { params: isAdmin ? { company_id: companyId } : {} });
      aplicar(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [companyId, isAdmin]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const guardar = async (cambios, mensaje) => {
    setGuardando(true);
    try {
      const { data } = await api.put('/task-notifications', cambios, { params });
      aplicar(data.data);
      toast.success(mensaje);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  if (!datos) return null;

  const listaCorreos = correos
    .split(/[,;\s]+/)
    .map(c => c.trim())
    .filter(Boolean);

  return (
    <div className='bg-white rounded-lg border border-slate-200 p-6 space-y-4'>
      <div>
        <div className='flex items-center mb-1'>
          <BellAlertIcon className='h-5 w-5 text-blue-600 mr-2' />
          <h3 className='text-lg font-semibold text-slate-800'>Avisos de tareas</h3>
        </div>
        <p className='text-sm text-slate-500'>
          Cuando ClaimUp, el despacho o alguien del equipo os deja una anotación como tarea, os avisamos al momento. La
          que va a toda la empresa llega a estos correos y a vuestro Slack; la que va a una persona, a su propio correo.
        </p>
      </div>

      <label className='flex items-center gap-2 text-sm text-slate-700'>
        <input
          type='checkbox'
          aria-label='Avisar de las tareas'
          checked={datos.enabled}
          disabled={guardando}
          onChange={e => guardar({ enabled: e.target.checked }, e.target.checked ? 'Avisos activados' : 'Avisos desactivados')}
        />
        Avisar cuando nos dejen una tarea
      </label>

      <div className='space-y-1'>
        <label htmlFor='taskNotificationEmails' className='block text-sm font-medium text-slate-700'>
          Correos para las tareas de toda la empresa
        </label>
        <div className='flex flex-wrap gap-2'>
          <input
            id='taskNotificationEmails'
            type='text'
            value={correos}
            onChange={e => setCorreos(e.target.value)}
            placeholder='cobros@empresa.es, jefa@empresa.es'
            className='flex-1 min-w-[16rem] rounded-lg border-slate-300 text-sm'
          />
          <button
            type='button'
            disabled={guardando}
            onClick={() => guardar({ emails: listaCorreos }, 'Correos guardados')}
            className='rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
          >
            Guardar correos
          </button>
        </div>
      </div>

      <div className='space-y-1'>
        <label htmlFor='taskNotificationSlack' className='block text-sm font-medium text-slate-700'>
          Webhook de Slack
        </label>
        <p className='text-xs text-slate-500'>
          En Slack, crea una «Incoming Webhook» en el canal donde queréis los avisos y pega aquí su URL. Quien la tenga
          puede escribir en ese canal: una vez guardada, solo se ve el final.
        </p>
        {datos.slack.set && (
          <p className='text-sm text-slate-700'>
            Conectado <span className='font-mono text-slate-500'>{datos.slack.hint}</span>{' '}
            <button
              type='button'
              disabled={guardando}
              onClick={() => guardar({ slack_webhook_url: null }, 'Slack desconectado')}
              className='ml-2 text-red-600 hover:underline'
            >
              Quitar
            </button>
          </p>
        )}
        <div className='flex flex-wrap gap-2'>
          <input
            id='taskNotificationSlack'
            type='password'
            autoComplete='off'
            value={webhook}
            onChange={e => setWebhook(e.target.value)}
            placeholder={datos.slack.set ? 'Pega otra URL para cambiarla' : 'https://hooks.slack.com/services/…'}
            className='flex-1 min-w-[16rem] rounded-lg border-slate-300 text-sm'
          />
          <button
            type='button'
            disabled={guardando || !webhook.trim()}
            onClick={() => guardar({ slack_webhook_url: webhook.trim() }, 'Slack conectado')}
            className='rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
          >
            Guardar Slack
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskNotifications;
